import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useHistory } from "react-router-dom";

import { makeStyles, withStyles } from "@material-ui/core/styles";

import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Grid from "@material-ui/core/Grid";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";
import Box from "@material-ui/core/Box";

import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import CardText from "components/Card/CardText";
import XLSIcon from "../../../assets/icons/excel-icon.svg";

import EnhancedTableHead, {
  stableSort,
  getComparator
} from "../../common/EnhancedTableHead";
import Loading from "../../common/Loading";

import { actionCreators as actionCreatorsAdmin } from "../../../_actions/adminActions";
import { CHAPTER_FINANCIAL_STATUS, CHAPTER_STATUS } from "../../../constants";
import { PAGE_SIZE } from "../../../constants";
import { yearList, currentYear } from "../../../utils";
import { getCurrentFiscalYear } from "../../../_services/chapter";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%"
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  },
  table: {
    minWidth: 750
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  cursorPointer: {
    cursor: "pointer"
  },
  filterDropdown: {
    minWidth: "120px"
  },
  expanded: {
    flex: 1
  },
  XLSIcon: {
    width: "20px",
    marginRight: "5px",
    marginTop: "-5px"
  },
  floatTopRight: {
    float: "right",
    marginTop: "-50px",
    cursor: "pointer",
    position: "relative",
    zIndex: 100
  }
}));

const headCells = [
  {
    id: "chapterName",
    numeric: false,
    disablePadding: false,
    label: "Chapter"
  },
  {
    id: "status",
    disablePadding: false,
    label: "Status"
  },
  {
    id: "updatedDate",
    disablePadding: false,
    label: "Updated Date"
  },
  {
    id: "updatedByUserName",
    disablePadding: false,
    label: "Updated By"
  },
  {
    id: "updatedByEmail",
    disablePadding: false,
    label: "Email"
  }
];

const StyledTableRow = withStyles(theme => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover
    }
  }
}))(TableRow);

const ChapterFinancials = props => {
  const classes = useStyles();
  const [fiscalYear, setFiscalYear] = useState(
    parseInt(localStorage.getItem("fiscalYear")) || currentYear
  );
  const [reportStatus, setReportStatus] = useState(
    parseInt(localStorage.getItem("reportStatus")) ||
      CHAPTER_FINANCIAL_STATUS.NONE
  );
  const [chapterFilter, setChapterFilter] = useState(
    parseInt(localStorage.getItem("chapterFilter")) || -1
  );
  const [fullChapterList, setFullChapterList] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("chapterName");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    parseInt(localStorage.getItem("chapterFinancialPageSize")) || PAGE_SIZE
  );

  const history = useHistory();

  const {
    resetIsFetched,
    getChapterFinancials,
    getChapterList,
    chapterFinancials,
    totalLength,
    isFetching,
    isFetched,
    chapterList,
    getFinancialReport
  } = props;

  useEffect(() => {
    (async () => {
      if (!isFetching.CHAPTER_FINANCIALS && !isFetched.CHAPTER_FINANCIALS) {
        await getChapterFinancials(fiscalYear);
      }
      if (!isFetching.CHAPTER_LIST && !isFetched.CHAPTER_LIST) {
        await getChapterList();
      }
    })();
  }, [fiscalYear]);

  useEffect(() => {
    if (chapterList && chapterList.length && chapterFinancials !== null) {
      setFullChapterList(
        chapterList
          .map(chapter => {
            const financial = chapterFinancials.find(
              x => x.chapter.id === chapter.id
            );
            if (!financial) {
              return {
                chapter,
                status: CHAPTER_FINANCIAL_STATUS.NOT_SUBMITTED,
                chapterName: chapter.name,
                updatedDate: new Date().setFullYear(1900).toLocaleString(),
                updatedByUserName: "N/A"
              };
            }
            return {
              ...financial,
              chapterName: financial.chapter.name
            };
          })
          .filter(
            row =>
              +reportStatus === CHAPTER_FINANCIAL_STATUS.NONE ||
              row.status === +reportStatus
          )
          .filter(
            row => chapterFilter === -1 || row.chapter.id === chapterFilter
          )
      );
    }
  }, [chapterList, chapterFinancials, reportStatus, chapterFilter]);

  const handleReportStatus = useCallback(({ target: { value } }) => {
    resetIsFetched();
    setReportStatus(value);
    localStorage.setItem("reportStatus", value);
    setPage(0);
  }, []);

  const handleFiscalYear = useCallback(({ target: { value } }) => {
    resetIsFetched();
    setFiscalYear(value);
    localStorage.setItem("fiscalYear", value);
    setPage(0);
  }, []);

  const handleChapterFilter = useCallback(({ target: { value } }) => {
    resetIsFetched();
    setChapterFilter(value);
    localStorage.setItem("chapterFilter", value);
    setPage(0);
  }, []);

  const handleViewMailLog = useCallback(
    (id, event) => {
      event.stopPropagation();
      history.push(`/admin/financial-report/${id}/mail-log`);
    },
    [history]
  );

  const handleViewReport = useCallback(
    (id, chapterId) => () => {
      if (id === undefined) {
        history.push(
          `/admin/financial-report/add?chapter=${chapterId}&year=${fiscalYear}`
        );
      } else {
        history.push(`/admin/financial-report/${id}`);
      }
    },
    [history, fiscalYear]
  );

  const handleRequestSort = useCallback(
    (event, property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [orderBy, order]
  );

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(({ target: { value } }) => {
    setRowsPerPage(parseInt(value, 10));
    localStorage.setItem("chapterFinancialPageSize", value);
    setPage(0);
  }, []);

  const paginate = useCallback(
    list => {
      return list.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    },
    [page, rowsPerPage]
  );

  const getStandardDate = useCallback(dateString => {
    const date = new Date(dateString);
    var newString = date.toLocaleString("en-US");
    if (newString === "Invalid Date") {
      return "N/A";
    } else {
      return newString;
    }
  }, []);

  const getUpdatedByUserName = useCallback(userName => {
    if (userName === undefined) {
      return "N/A";
    } else {
      return userName;
    }
  }, []);

  const handleExcelExport = useCallback(() => {
    getFinancialReport(reportStatus, fiscalYear);
  }, [history, fiscalYear, reportStatus]);

  return (
    <Container>
      <Card>
        <CardHeader color="primary" text>
          <CardText color="primary">
            <Typography variant="h4">Chapter Financial Reports</Typography>
          </CardText>
        </CardHeader>
        <CardBody>
          <Grid
            item
            className={classes.floatTopRight}
            onClick={handleExcelExport}
          >
            <img src={XLSIcon} className={classes.XLSIcon} />
            Download XLS
          </Grid>
          <Grid item className={classes.marginBottom}></Grid>
          <Box display="flex" justifyContent="space-between">
            <FormControl variant="outlined" className={classes.filterDropdown}>
              <InputLabel id="chapter-select">Chapter</InputLabel>
              <Select
                labelId="chapter-select"
                value={chapterFilter}
                onChange={handleChapterFilter}
                label="Chapter"
                MenuProps={{ classes: { paper: classes.menuPaper } }}
              >
                <MenuItem value={-1}>All</MenuItem>
                {chapterList &&
                  chapterList.map(chapter => (
                    <MenuItem value={chapter.id} key={`chapter-${chapter.id}`}>
                      {chapter.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Box display="flex">
              <FormControl
                variant="outlined"
                className={classes.filterDropdown}
              >
                <InputLabel id="report-status-select">Report Status</InputLabel>
                <Select
                  labelId="report-status-select"
                  value={reportStatus}
                  onChange={handleReportStatus}
                  label="Report Status"
                  MenuProps={{ classes: { paper: classes.menuPaper } }}
                >
                  <MenuItem value={CHAPTER_FINANCIAL_STATUS.NONE}>
                    None
                  </MenuItem>
                  {Object.keys(CHAPTER_STATUS).map(key => (
                    <MenuItem value={key} key={`rs-${key}`}>
                      {CHAPTER_STATUS[key]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box mr={2} />
              <FormControl
                variant="outlined"
                className={classes.filterDropdown}
              >
                <InputLabel id="fiscal-year-select">Fiscal Year</InputLabel>
                <Select
                  labelId="fiscal-year-select"
                  value={fiscalYear}
                  onChange={handleFiscalYear}
                  label="Fiscal Year"
                  MenuProps={{ classes: { paper: classes.menuPaper } }}
                >
                  {yearList.map(value => (
                    <MenuItem value={value} key={`fiy-${value}`}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <TableContainer>
            <Table
              className={classes.table}
              aria-labelledby="tableTitle"
              size="medium"
              aria-label="enhanced table"
            >
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={totalLength}
                headCells={headCells}
              />
              <TableBody>
                {isFetching.CHAPTER_FINANCIALS || getCurrentFiscalYear === 0 ? (
                  <StyledTableRow>
                    <TableCell colSpan={5} align="center">
                      <Loading />
                    </TableCell>
                  </StyledTableRow>
                ) : !fullChapterList || fullChapterList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No data
                    </TableCell>
                  </TableRow>
                ) : (
                  paginate(
                    stableSort(fullChapterList, getComparator(order, orderBy))
                  ).map((row, index) => {
                    const labelId = `enhanced-table-row-${index}`;

                    return (
                      <StyledTableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={labelId}
                        className={classes.cursorPointer}
                        onClick={handleViewReport(row.id, row.chapter.id)}
                      >
                        <TableCell component="th" scope="row">
                          {row.chapter.name}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {CHAPTER_STATUS[row.status]}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {getStandardDate(row.updatedDate)}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {getUpdatedByUserName(row.updatedByUserName)}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {row.updatedByEmail}
                        </TableCell>

                        <TableCell
                          component="th"
                          scope="row"
                          align="right"
                          onClick={
                            row.mailLog && row.mailLog.length > 0
                              ? e => handleViewMailLog(row.id, e)
                              : null
                          }
                        >
                          {row.mailLog && row.mailLog.length > 0
                            ? "Mail Log"
                            : ""}
                        </TableCell>
                      </StyledTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={fullChapterList.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </CardBody>
      </Card>
    </Container>
  );
};

const mapStateToProps = state => ({
  chapterFinancials: state.adminReducer.chapterFinancials,
  chapterList: state.adminReducer.chapterList,
  totalLength: state.adminReducer.totalChapters,
  isFetching: state.adminReducer.isFetching,
  isFetched: state.adminReducer.isFetched
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actionCreatorsAdmin }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ChapterFinancials);
